import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Table, Column, HeaderCell, Cell } from 'rsuite-table'
import 'rsuite-table/dist/css/rsuite-table.css'
import './styles.css'

const SHADOW_HEADER_CLASS = 'shadow-table-header'
const SHADOW_FIXED_CLASS = 'shadow-fixed'
const SCROLL_BOTTOM_SHADOW_CLASS = 'scrollbar-bottom-shadow'
const HIDE_SHADOW_CLASS = 'hide-shadow'
const HEADER_HEIGHT = 45
const ROW_HEIGHT = 40
const HEADER_AND_SCROLL_SIZE = 30
const PAGE_CHROME = 390
const DEFAULT_SCREEN_HEIGHT = Math.max(window.innerHeight - PAGE_CHROME, 200)
const ACTIVE_TARGET_COLUMN_WIDTH = 110
const ACTIONS_COLUMN_WIDTH = 72

const columns = {
  name: {
    label: 'externalApisManagement.table.name',
    field: 'name',
    customWidth: 120,
  },
  group: {
    label: 'externalApisManagement.table.group',
    field: 'group',
    customWidth: 170,
  },
  urlDev: {
    label: 'externalApisManagement.table.urlDev',
    field: 'urlDev',
    customWidth: 200,
    flexGrow: 1,
  },
  urlPrd: {
    label: 'externalApisManagement.table.urlPrd',
    field: 'urlPrd',
    customWidth: 200,
    flexGrow: 1,
  },
  apiKeyDev: {
    label: 'externalApisManagement.table.apiKeyDev',
    field: 'apiKeyDev',
    customWidth: 150,
  },
  apiKeyPrd: {
    label: 'externalApisManagement.table.apiKeyPrd',
    field: 'apiKeyPrd',
    customWidth: 150,
  },
  usesVaultLabel: {
    label: 'externalApisManagement.table.syncVault',
    field: 'usesVaultLabel',
    customWidth: 90,
    align: 'center',
  },
}

const COPYABLE_FIELDS = ['urlDev', 'urlPrd', 'apiKeyDev', 'apiKeyPrd']

const TargetSwitchCell = ({ rowData, onToggle, disabled }) => {
  const isPrd = rowData.activeTarget === 'PRD'

  return (
    <div className='external-apis-target-switch'>
      <label className={`external-apis-target-switch-label ${disabled ? 'external-apis-target-switch-label-disabled' : ''}`}>
        <div className='switch-container'>
          <input
            type='checkbox'
            checked={isPrd}
            className='switch-input'
            disabled={disabled}
            onChange={() => onToggle(rowData)}
          />
          <span className='slider' />
        </div>
        <span className={`external-apis-target-text ${isPrd ? 'external-apis-target-prd' : 'external-apis-target-dev'}`}>
          {isPrd ? 'PRD' : 'DEV'}
        </span>
      </label>
    </div>
  )
}

TargetSwitchCell.propTypes = {
  rowData: PropTypes.object,
  onToggle: PropTypes.func,
  disabled: PropTypes.bool,
}

const ExternalApisTable = ({ items, translate, onEdit, onView, onToggleTarget, togglingIds = [], children }) => {
  const shadowRef = useRef(null)
  const wrapperRef = useRef(null)
  const [shadowWidth] = useState(0)
  const [tableHeight, setTableHeight] = useState(400)
  const [copiedCell, setCopiedCell] = useState(null)

  const dataList = items.map((item, index) => ({
    ...item,
    uniqueId: index,
    usesVaultLabel: item.usesVault
      ? translate('externalApisManagement.target.vault')
      : translate('externalApisManagement.target.custom'),
  }))

  useEffect(() => {
    const dynamicSize = dataList.length <= 2 ? 120 : dataList.length * ROW_HEIGHT
    if (dynamicSize > DEFAULT_SCREEN_HEIGHT) {
      setTableHeight(DEFAULT_SCREEN_HEIGHT + HEADER_AND_SCROLL_SIZE)
    } else {
      setTableHeight(dynamicSize + HEADER_HEIGHT + 10)
    }
    if (window.innerHeight < 745) {
      setTableHeight(300)
    }
  }, [dataList.length])

  const getBodyWrapper = () =>
    wrapperRef.current?.querySelector('.rs-table-body-row-wrapper')

  const getTableHeader = () =>
    wrapperRef.current?.querySelector('.rs-table-row-header')

  const updateFooterShadow = (scrollY = null) => {
    const wrapperElement = wrapperRef.current
    if (!wrapperElement) {
      return
    }

    const footerShadow = wrapperElement.querySelector('.box-footer-shadow')
    const verticalScrollbar = wrapperElement.querySelector('.rs-table-scrollbar-vertical')
    const horizontalScrollbar = wrapperElement.querySelector('.rs-table-scrollbar-horizontal')

    if (!footerShadow) {
      return
    }

    footerShadow.style.top = horizontalScrollbar ? '-4px' : '5px'

    if (!verticalScrollbar) {
      footerShadow.classList.add(HIDE_SHADOW_CLASS)
      footerShadow.classList.remove(SCROLL_BOTTOM_SHADOW_CLASS)
      return
    }

    const bodyWrapper = getBodyWrapper()
    if (!bodyWrapper) {
      footerShadow.classList.add(HIDE_SHADOW_CLASS)
      footerShadow.classList.remove(SCROLL_BOTTOM_SHADOW_CLASS)
      return
    }

    const maxScrollTop = bodyWrapper.scrollHeight - bodyWrapper.clientHeight
    const currentScrollTop = typeof scrollY === 'number' ? scrollY : bodyWrapper.scrollTop
    const isAtBottom = currentScrollTop >= maxScrollTop - 20

    if (isAtBottom) {
      footerShadow.classList.add(HIDE_SHADOW_CLASS)
      footerShadow.classList.remove(SCROLL_BOTTOM_SHADOW_CLASS)
    } else {
      footerShadow.classList.add(SCROLL_BOTTOM_SHADOW_CLASS)
      footerShadow.classList.remove(HIDE_SHADOW_CLASS)
    }
  }

  const handleScroll = (scrollX, scrollY) => {
    if (shadowRef.current) {
      if (scrollX > 0 && !shadowRef.current.classList.contains(SHADOW_FIXED_CLASS)) {
        shadowRef.current.classList.add(SHADOW_FIXED_CLASS)
      } else if (scrollX < 2) {
        shadowRef.current.classList.remove(SHADOW_FIXED_CLASS)
      }
    }

    const tableHeader = getTableHeader()
    if (tableHeader && scrollY >= 30 && !tableHeader.classList.contains(SHADOW_HEADER_CLASS)) {
      tableHeader.classList.add(SHADOW_HEADER_CLASS)
    } else if (tableHeader && scrollY < 30 && tableHeader.classList.contains(SHADOW_HEADER_CLASS)) {
      tableHeader.classList.remove(SHADOW_HEADER_CLASS)
    }

    updateFooterShadow(scrollY)
  }

  const handleCopyValue = (rowData, field) => {
    const value = rowData[field]
    if (!value) {
      return
    }

    navigator.clipboard.writeText(value).then(() => {
      const cellId = `${rowData.id}-${field}`
      setCopiedCell(cellId)
      window.setTimeout(() => {
        setCopiedCell((current) => (current === cellId ? null : current))
      }, 1500)
    })
  }

  useEffect(() => {
    const refreshLayout = () => {
      window.dispatchEvent(new Event('resize'))
      updateFooterShadow()
    }

    const immediateTimer = window.setTimeout(refreshLayout, 0)
    const delayedTimer = window.setTimeout(refreshLayout, 150)

    return () => {
      window.clearTimeout(immediateTimer)
      window.clearTimeout(delayedTimer)
    }
  }, [items, tableHeight])

  useEffect(() => {
    const bodyWrapper = getBodyWrapper()
    if (!bodyWrapper) {
      return undefined
    }

    const syncFooterShadow = () => updateFooterShadow(bodyWrapper.scrollTop)
    syncFooterShadow()
    bodyWrapper.addEventListener('scroll', syncFooterShadow)

    return () => bodyWrapper.removeEventListener('scroll', syncFooterShadow)
  }, [items, tableHeight])

  return (
    <>
      <div
        ref={shadowRef}
        style={{ height: '1px', width: `${shadowWidth}px` }}
        className='shadow'
      >
        &nbsp;
      </div>

      <div ref={wrapperRef} className='wrapper-table-relatory'>
        <Table
          data={dataList}
          height={tableHeight}
          headerHeight={HEADER_HEIGHT}
          minHeight={150}
          rowHeight={ROW_HEIGHT}
          cellBordered={false}
          rowClassName='relatory-table-row'
          onScroll={handleScroll}
        >
          {Object.keys(columns).map((key, index) => {
            const col = columns[key]
            const isLast = index === Object.keys(columns).length - 1
            const isFlexibleColumn = Boolean(col.flexGrow)

            return (
              <Column
                key={key}
                align={col.align || 'left'}
                verticalAlign='middle'
                fixed={false}
                width={isFlexibleColumn ? undefined : col.customWidth}
                minWidth={isFlexibleColumn ? col.customWidth : undefined}
                flexGrow={isFlexibleColumn ? col.flexGrow : undefined}
                resizable={isFlexibleColumn ? false : !isLast}
              >
                <HeaderCell
                  className={`custom-header-relatory ${index === 0 ? 'first-col-report' : ''}`}
                >
                  {translate(col.label)}
                </HeaderCell>
                <Cell>
                  {(rowData) => {
                    const value = rowData[col.field]
                    const isCopyable = COPYABLE_FIELDS.includes(col.field) && value
                    const cellId = `${rowData.id}-${col.field}`

                    if (!isCopyable) {
                      return value
                    }

                    return (
                      <span
                        className={`external-apis-copyable-text ${
                          copiedCell === cellId ? 'external-apis-copyable-text-copied' : ''
                        }`}
                        onClick={() => handleCopyValue(rowData, col.field)}
                        title={translate('externalApisManagement.table.copyValue')}
                      >
                        {value}
                      </span>
                    )
                  }}
                </Cell>
              </Column>
            )
          })}

          <Column
            width={ACTIVE_TARGET_COLUMN_WIDTH}
            align='center'
            verticalAlign='middle'
            fixed={false}
          >
            <HeaderCell className='custom-header-relatory'>
              {translate('externalApisManagement.table.activeTarget')}
            </HeaderCell>
            <Cell>
              {(rowData) => (
                <TargetSwitchCell
                  rowData={rowData}
                  onToggle={onToggleTarget}
                  disabled={togglingIds.includes(rowData.id)}
                />
              )}
            </Cell>
          </Column>

          <Column
            width={ACTIONS_COLUMN_WIDTH}
            align='center'
            verticalAlign='middle'
            fixed='right'
          >
            <HeaderCell className='custom-header-relatory'>
              {translate('externalApisManagement.table.actions')}
            </HeaderCell>
            <Cell>
              {(rowData) => (
                <div className='external-apis-actions-cell'>
                  <i
                    onClick={() => onView(rowData)}
                    style={{ cursor: 'pointer' }}
                    className='glyph glyph-eye icon-size color-primary'
                  />
                  <i
                    onClick={() => onEdit(rowData)}
                    style={{ cursor: 'pointer' }}
                    className='glyph glyph-pencil icon-size color-primary'
                  />
                </div>
              )}
            </Cell>
          </Column>
        </Table>

        <div className='box-footer-shadow hide-shadow'>&nbsp;</div>

        {children}
      </div>
    </>
  )
}

ExternalApisTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  translate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onToggleTarget: PropTypes.func.isRequired,
  togglingIds: PropTypes.array,
  children: PropTypes.node,
}

export default ExternalApisTable
